import Quote from './viewer-quote';
import Watermark from './viewer-watermark';
import _userAgent from './useragent';

export default class ViewerCore {
    constructor(mainViewerId, tileSourceDef, initialPage, watermarkUrl, watermarkPosition, quoteCallback, loadingOverlayId) {
        if (!mainViewerId) throw new Error('mainViewerIdが指定されていません。');
        if (!tileSourceDef) throw new Error('tileSourceDefが指定されていません。');

        /**
         * メインビューア
         * @type {OpenSeadragon.Viewer}
         */
        this.mainViewer = null;

        this._mainViewerId = mainViewerId;

        /**
         * 総ページ数。 
         * @type {Number}
         */
        this.totalPagesCount = tileSourceDef.length;
        /**
         * 保持しているイベントリスナーリスト。 
         * @type {Array<String>}
         */
        this._listeners = {};
        /**
         * ウォーターマークオブジェクト。
         * @private
         * @type {JGMWatermark}
         */
        this._watermark;
        /**
         * ウォーターマークのURL
         * @private
         * @type {String}
         */
        this._watermarkUrl = watermarkUrl;
        /**
         * ウォーターマークの表示位置
         * @private
         * @type {Number}
         */
        this._watermarkPosition = watermarkPosition;
        /**
         * ビューアを同期するイベントハンドラを保持する変数。
         * @private
         * @type {Function}
         */
        this._navigatorHandler = null;
        /**
         * サブ画像のウォーターマーク
         * @private
         * @type {JGMWatermark}
         */
        this._subWatermark = null;
        /**
         * 描写をすぐに行うか否か
         * @private
         * @type {Boolean}
         */
        this._immediateRender = _userAgent === 'ios' || _userAgent === 'android';

        if (loadingOverlayId) {
            this._loadTimer = null;
            this._loadOverlay = document.getElementById(loadingOverlayId);
            this.showLoadAnimation();
        }

        // メインビューアの生成。
        this.mainViewer = new OpenSeadragon({
            id: mainViewerId,
            tileSources: tileSourceDef,
            sequenceMode: true,
            initialPage: initialPage - 1 || 0,
            immediateRender: this._immediateRender,
            showSequenceControl: false,
            showFullPageControl: false,
            showHomeControl: false,
            showZoomControl: false,
            showNavigator: true,
            navigatorId: 'navigator',
            navigatorPosition: 'ABSOLUTE',
            navigatorRotate: false,
            navigatorAutoFade: false,
            navigatorAutoResize: false,
            gestureSettingsMouse: {
                clickToZoom: false,
                dblClickToZoom: true
            },
            crossOriginPolicy: 'Anonymous'
        });

        // ウォーターマークの設定。
        this.hasWatermark = watermarkUrl !== ' ' && watermarkUrl !== null && watermarkUrl !== undefined && watermarkPosition !== undefined;
        if (this.hasWatermark) this._watermark = new Watermark(this.mainViewer, this._watermarkUrl, this._watermarkPosition, null);

        this._mainQuoteHelper = new Quote(this.mainViewer, quoteCallback);

        // ビューアが開かれた際の処理。
        this.mainViewer.addOnceHandler('open', this._init.bind(this));
        this.mainViewer.addOnceHandler('open', this._updateNavigator.bind(this));

        if (loadingOverlayId) this.mainViewer.addHandler('tile-drawing', this.hiddenLoadAnimation.bind(this));
    }

    /**
     * ビューアの初期化を行います。
     */
    _init() {
        // アスペクト比が10:1以上の場合は巻物と判定し、初期表示位置設定を行う。
        if (this.mainViewer.source.aspectRatio > 10) this.goHomeForScrollsItem(true);

        // ビューアサイズが変更された場合にナビゲーターのサイズも変更されるように登録する。
        this.mainViewer.addHandler('resize', this._updateNavigator.bind(this));
        //if (this._initializeNavView === 0) this.toggleDisplayOfNavigator();
        this.toggleDisplayOfNavigator();

        // 初期状態を通利するためにイベントを発生させる。
        this.raiseEvent('changeRotate', { degrees: this.getRotation() });
        this.raiseEvent('changePage', { page: this.getCurrentPage(), existNextPage: this.existNextPage() });
    }

    /**
     * ローディングアイコンを表示します。
     */
    showLoadAnimation() {
        this._loadTimer = setTimeout(function () {
            this._loadOverlay.setAttribute('aria-hidden', 'false');
        }.bind(this), 200);
    }

    /**
     * ローディングアイコンを非表示にします。
     */
    hiddenLoadAnimation() {
        clearTimeout(this._loadTimer);
        this._loadOverlay.setAttribute('aria-hidden', 'true');
    }

    getOriginalImageSize() {
        
        const tileSource = this.mainViewer.world.getItemAt(0).source;

        return new OpenSeadragon.Point(tileSource.width, tileSource.height);
    }

    /**
     * 現在のページ番号を取得します。
     * @return {Number} 現在のページ番号。
     */
    getCurrentPage() {
        
        return this.mainViewer.currentPage() + 1;
    }

    /**
     * 現在のページ番号(インデックス)を取得します。
     * @return {Number} 現在のページ番号(インデックス)。
     */
    getIndexOfCurrentPage() {
        
        return this.mainViewer.currentPage();
    }

    /**
     * 指定したページに移動します。
     * @param {Number} page 移動先のページ番号。
     * @param {Boolean} [needRaiseEvent=true] イベントを発火させる必要性の有無。
     * 
     * @throws {RangeError} 指定されたページが存在しない場合に発生します。
     */
    goToPage(page, needRaiseEvent) {

        const index = page - 1;
        if (index === this.getIndexOfCurrentPage()) return;
        if (index < 0 || this.totalPagesCount < index) throw new RangeError('specified page does not exist');

        this.showLoadAnimation();
        this.mainViewer.goToPage(index);

        const raiseEvent = needRaiseEvent === null || needRaiseEvent === undefined ? true : needRaiseEvent;
        if (raiseEvent) this.raiseEvent('changePage', { page: page, existNextPage: this.existNextPage() });
    }

    /**
     * 次のページが存在するか否かを判定します。
     * @returns {Boolean} 次のページの有無。
     */
    existNextPage () {

        return this.getCurrentPage() < this.totalPagesCount;
    }

    /**
     * 画像の表示を指定した倍率に変更します。
     * @param {Number} [zoom=2.0] 拡大倍率。
     */
    zoomTo(zoom) {

        zoom = zoom || 2.0;
        this.mainViewer.viewport.zoomBy(zoom);
        this.mainViewer.viewport.applyConstraints();
    }

    /**
     * 画像を横幅に合わせます。
     * @param {Boolean} [immediately=false] 直ちに実行するか否か（＝アニメーションの有無）。
     */
    fitHorizontally(immediately) {

        immediately = immediately === true;

        const degrees = this.getRotation();
        const center = this.mainViewer.viewport.getCenter(immediately);

        const newBounds = degrees !== 90 && degrees !== 270
            ? new OpenSeadragon.Rect(0, center.y, 1, 0)
            : new OpenSeadragon.Rect(center.x, 0, 0, 1 / this.mainViewer.source.aspectRatio);

        this.mainViewer.viewport.fitBounds(newBounds, immediately);
    }

    /**
     * 画像を縦幅に合わせます。
     * @param {Boolean} [immediately=false] 直ちに実行するか否か（＝アニメーションの有無）。
     */
    fitVertically(immediately) {

        immediately = immediately === true;

        const degrees = this.getRotation();
        const center = this.mainViewer.viewport.getCenter(immediately);

        const newBounds = degrees !== 90 && degrees !== 270
            ? new OpenSeadragon.Rect(center.x, 0, 0, 1 / this.mainViewer.source.aspectRatio)
            : new OpenSeadragon.Rect(0, center.y, 1, 0);

        this.mainViewer.viewport.fitBounds(newBounds, immediately);
    }

    /**
     * 画像を全体表示します。
     * @param {Boolean} [immediately=false] 直ちに実行するか否か（＝アニメーションの有無）。
     */
    goHome(immediately) {

        immediately = immediately === true;

        if (this.mainViewer.source.aspectRatio > 10) {
            this.goHomeForScrollsItem(immediately);
        }
        else {
            this.mainViewer.viewport.goHome(immediately);
        }
    }

    /**
     * 巻物のホーム画面に移動させます。
     * @param {Boolean} [immediately=false] 直ちに実行するか否か（＝アニメーションの有無）。
     */
    goHomeForScrollsItem(immediately) {

        immediately = immediately === true;

        this.fitVertically(true);
        const viewport = this.mainViewer.viewport;

        // 現在のビューポートの矩形座標(x, y, width, height)
        const bounds = viewport.getBounds(true);

        // 現在のビューポートの中心点(x,y)
        const center = viewport.getCenter(true);

        viewport.panTo({ x: 1 - bounds.width / 2, y: center.y }, immediately);
    }

    /**
 * 画像の回転角を取得します。
 * @returns {Number} 画像の回転角。
 */
    getRotation() {
        

        let degrees = this.mainViewer.viewport.getRotation() % 360;
        if (degrees < 0) degrees += 360;

        return degrees;
    }

    /**
     * 画像を指定した角度に設定します。
     * @param {Number} [degrees=0] 回転角度。
     * @param {Boolean} [needRaiseEvent=true] イベントを発火させる必要の有無。
     */
    setRotation(degrees, needRaiseEvent) {
        
        degrees = degrees || 0;
        this.mainViewer.viewport.setRotation(degrees);

        const raiseEvent = needRaiseEvent === null || needRaiseEvent === undefined ? true : needRaiseEvent;
        if (raiseEvent) this.raiseEvent('changeRotate', { degrees: this.getRotation() });
    }

    /**
     * 画像を指定した角度回転させます。
     * @param {Number} offset 回転させる角度。
     * @param {Boolean} [needRaiseEvent=true] イベントを発火させる必要の有無。
     */
    setRotate(offset, needRaiseEvent) {
        
        let degrees = offset + this.getRotation();
        degrees = (degrees + 360) % 360;

        this.setRotation(degrees, needRaiseEvent);
    }

    /**
     * 現在の表示がフルスクリーンであるか否かを取得します。
     * @return {Boolean} フルスクリーンであるか否か。
     */
   getFullscreenState () {

        if (document.webkitFullscreenElement) {
            return document.getElementById(this._mainViewerId).webkitCurrentFullScreenElement !== null;
        }
        else if (document.mozFullScreenElement) {
            return document.getElementById(this._mainViewerId).mozFullScreenElement !== null;
        }
        else if (document.msFullscreenElement) {
            return document.getElementById(this._mainViewerId).msFullscreenElement !== null;
        }
        else if (document.fullscreenElement) {
            return document.getElementById(this._mainViewerId).fullscreenElement !== null;
        }

        return false;
    };


    /**
     * フルスクリーン表示に切り替えます。
     */
   toggleFullScreen () {

        const isFullScreen = this.getFullscreenState();

        if (isFullScreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
            else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
            else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            }
        }
        else {
            if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen();
            }
            else if (document.documentElement.msRequestFullscreen) {
                document.documentElement.msRequestFullscreen();
            }
            else if (document.documentElement.webkitRequestFullscreen) {
                document.documentElement.webkitRequestFullscreen();
            }
            else if (document.documentElement.mozRequestFullScreen) {
                document.documentElement.mozRequestFullScreen();
            }
        }

        setTimeout(function () {
            this.mainViewer.raiseEvent('resize');
        }.bind(this), 300);

        this.raiseEvent('changeFullscreenState', { isFullscreen: !isFullScreen });
    };

    /**
     * ナビゲーターの表示/非表示を判定します。
     * @return {Boolean} ナビゲータ表示の有無。
     */
   getVisibleOfNavigator () {

        const navigator = this.mainViewer.navigator;
        const isHidden = navigator.element.getAttribute('aria-hidden') === 'true';

        return !isHidden;
    }

    /**
     * ナビゲーターの表示/非表示を切り替えます。
     */
   toggleDisplayOfNavigator () {

        const navigator = this.mainViewer.navigator;
        const isHidden = navigator.element.getAttribute('aria-hidden') === 'true';
        navigator.element.setAttribute('aria-hidden', !isHidden);
        navigator.setVisible(isHidden);

        if (isHidden) {
            navigator.update(this.mainViewer.viewport);
            navigator.forceRedraw();
        }

        this.mainViewer.raiseEvent('resize');
    };

    /**
     * ナビゲータのサイズ調整を行います。
     */
   _updateNavigator () {

        const containerSize = OpenSeadragon.getElementSize(this.mainViewer.element.parentNode);
        const parentContainerSize = OpenSeadragon.getElementSize(this.mainViewer.element.parentNode.parentNode);

        const aspectRatio = this.mainViewer.source.aspectRatio;

        let width, height, left, top;
        if (aspectRatio > 10) {
            width = containerSize.x;
            height = Math.min(containerSize.y * 0.1, width / aspectRatio + 30);
            left = 0;
        }
        else {
            width = Math.round(containerSize.x * 0.2);
            height = Math.round(containerSize.y * 0.2);
            left = parentContainerSize.x - width - 4;
        }

        this.mainViewer.navigator.element.style.width = width + 'px';
        this.mainViewer.navigator.element.style.height = height + 'px';
        this.mainViewer.navigator.element.style.top = '0px';
        this.mainViewer.navigator.element.style.left = left + 'px';

       setTimeout(() => {
           this.mainViewer.navigator.updateSize();
           this.mainViewer.navigator.update(this.mainViewer.viewport);
       }, 250);    
    }

    /**
     * 引用の状態を設定します。
     * @param {Boolean} enabled 引用の状態。
     */
   setSelectionState (enabled) {
        this._mainQuoteHelper.setSelectionState(enabled);
        if (this._subQuoteHelper) this._subQuoteHelper.setSelectionState(enabled);
    }

    /**
     * 引用を確定します。 
     */
    confirmSelection() {
        this._mainQuoteHelper.confirmSelection();
    }

    /**
     * 引用を中止します。 
     */
   cancelSelection () {
        this._mainQuoteHelper.cancelSelection();
        if (this._subQuoteHelper) this._subQuoteHelper.cancelSelection();
    }

   getSelectedRectSize () {
        return this._mainQuoteHelper.getSelectionSize();
    }

   getCurrentPageImageId () {
        const params = this.mainViewer.world.getItemAt(0).source['@id'].split('/');
        const imageId = params.pop();

        return imageId.replace('.jp2', '');
    }

   registUrl (url) {

        this._mainQuoteHelper.isDrawing();
        return this._mainQuoteHelper.regist(url, url);
    }

    /**
     * イベントリスナーを登録します。
     * @param {String} type イベントの種類。
     * @param {EventListenerOrEventListenerObject} callback コールバック。
     */
   addEventListener (type, callback) {
        if (!(type in this._listeners)) {
            this._listeners[type] = [];
        }
        this._listeners[type].push(callback);
    }

    /**
     * イベントリスナーを削除します。
     * @param {String} type イベントの種類
     * @param {EventListenerOrEventListenerObject} [callback] コールバック
     */
   removeEventListener (type, callback) {
        if (!(type in this._listeners)) return;

        const stack = this._listeners[type];
        if (callback === null || callback === undefined) {
            stack.splice(0);
        }
        else {
            for (var i = 0, length = stack.length; i < length; i++) {
                if (stack[i] === callback) {
                    stack.splice(i, 1);
                    this.removeEventListener(type, callback);
                }
            }
        }
    }

    /**
     * イベントを発火します。
     * @param {CustomEvent} event イベントオブジェクト。
     */
   dispatchEvent (event) {

        if (!(event.type in this._listeners)) return;

        var stack = this._listeners[event.type];

        // event.target = this;
        for (var i = 0, l = stack.length; i < l; i++) {
            stack[i].call(this, event);
        }
    }

    /**
     * イベントを発火させます。
     * @param {String} eventName イベント名
     * @param {any} eventArgs detailに入る変数。
     */
   raiseEvent (eventName, eventArgs) {

        if (_userAgent !== 'ie') {
            const event = new CustomEvent(eventName, { detail: eventArgs });
            this.dispatchEvent(event);
        }
        else {
            const event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventName, false, false, eventArgs);
            this.dispatchEvent(event);
        }
    }
}